/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { forwardRef, useRef, type ForwardedRef } from "react";
import { mergeProps, useButton, useFocusRing, usePress } from "react-aria";

import { cssFns, type Style } from "@superweb/css";
import { icons, useTypo, useUiColors } from "@superweb/ui";

import { useMessage } from "#intl";

import type { ExpandColumn, FlatItem } from "../types";
import { useCellStyle } from "./cells";

export const ExpandCell = forwardRef(
  <D extends Object>(
    {
      column,
      row,
      rowIndex,
      onPress,
      isHovered = false,
      isStickyFirstColumn = false,
      stickyFirstColumnShift = 0,
      index,
      tabIndex,
      updateFocusPointer,
      variant = "fill",
      position,
      isTopPinned = false,
      isBottomPinned = false,
      isLastRow = false,
      isFirstColumn = false,
      isExpanded = false,
      getUpdatedExpandedRows,
    }: {
      index: number;
      tabIndex?: number;
      column: ExpandColumn<D>;
      row: FlatItem<D>;
      rowIndex: number;
      onPress?: () => void;
      isHovered?: boolean;
      isTopPinned?: boolean;
      isBottomPinned?: boolean;
      isStickyFirstColumn?: boolean;
      stickyFirstColumnShift?: number;
      updateFocusPointer: () => void;
      variant?: "fill" | "ghost";
      isLastRow?: boolean;
      position: {
        firstLeft?: boolean;
        firstRight?: boolean;
        lastLeft?: boolean;
        lastRight?: boolean;
      };
      isFirstColumn?: boolean;
      isExpanded?: boolean;
      getUpdatedExpandedRows: (row: FlatItem<D>) => string[];
    },
    ref: ForwardedRef<HTMLTableCellElement>,
  ) => {
    const typo = useTypo();
    const message = useMessage();
    const uiColors = useUiColors();

    const { focusProps, isFocusVisible } = useFocusRing();

    const expand = () => {
      row.childrenCount
        ? column.onExpand(getUpdatedExpandedRows(row))
        : undefined;
    };

    const { pressProps } = usePress({
      onPress: (event) => {
        updateFocusPointer();

        if (row.childrenCount && event.pointerType === "keyboard") {
          expand();
        } else {
          onPress?.();
        }
      },
    });

    const buttonRef = useRef<HTMLButtonElement>(null);
    const { buttonProps } = useButton(
      {
        onPress: expand,
        "aria-label": message({
          id: "497d1225-90b3-4538-8061-d2a3f5625440",
          context: "Table row. Expand button aria label",
          default: "Expand row",
        }),
        excludeFromTabOrder: true,
      },
      buttonRef,
    );

    const cellProps = onPress ? mergeProps(focusProps, pressProps) : focusProps;

    const cellStyle = useCellStyle({
      index,
      variant,
      align: column.align,
      isHovered,
      isFocusVisible,
      isStickyFirstColumn,
      stickyFirstColumnShift,
      isTopPinned,
      isBottomPinned,
      isLastRow,
      clickable: Boolean(onPress),
      position,
    });

    const style: Style = {
      ...cssFns.overflow("hidden"),
      textOverflow: "ellipsis",
      display: "block",
      ...(variant === "ghost" && { lineHeight: "24px" }),
    };

    const expandingRowDot = isFirstColumn && row.path.length > 1 && (
      <div
        css={{ color: uiColors.line, display: "flex", alignItems: "center" }}
      >
        <icons.DotM width={24} height={24} />
      </div>
    );

    return (
      <td css={cellStyle} aria-colindex={index} ref={ref}>
        <div
          {...cellProps}
          tabIndex={tabIndex}
          css={{
            outlineStyle: "none",
            ...cssFns.padding("16px"),
            ...style,

            display: "flex",
            alignItems: "center",
          }}
        >
          {expandingRowDot}
          <span css={cssFns.overflow("hidden")}>
            {column.render(row, rowIndex)}
          </span>
          {row.childrenCount ? (
            <button
              css={{
                appearance: "none",
                ...cssFns.margin("0"),
                ...cssFns.padding("0"),
                ...cssFns.border({ style: "none" }),
                backgroundColor: "transparent",
                cursor: "pointer",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                columnGap: "4px",
                color: uiColors.textMinor,
                ...typo({
                  level: "body2",
                  density: "tight",
                  weight: "regular",
                }),
                marginInlineStart: "8px",
              }}
              {...buttonProps}
            >
              {column.getExpandedButtonText(row)}
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "14px",
                  height: "14px",
                }}
              >
                <div css={{ width: "24px", height: "24px" }}>
                  {isExpanded ? (
                    <icons.ChevronUpM width={24} height={24} />
                  ) : (
                    <icons.ChevronDownM width={24} height={24} />
                  )}
                </div>
              </div>
            </button>
          ) : undefined}
        </div>
      </td>
    );
  },
);
